import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { formatDistanceToNow, format, isSameDay, startOfMonth, endOfMonth, addMonths, subMonths } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { db } from '../../services/firebase';
import { useAuthContext } from '../../contexts/AuthContext';

const PAGE_SIZE = 20;
/** Firestore page size while walking the full history (dropdown still uses its own limit). */
const FETCH_BATCH = 300;

const toDate = (timestamp) => {
  if (!timestamp) return null;
  if (typeof timestamp?.toDate === 'function') return timestamp.toDate();
  const d = new Date(timestamp);
  return Number.isNaN(d.getTime()) ? null : d;
};

const HistoryPage = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(() => startOfMonth(new Date()));

  const loadHistory = useCallback(async () => {
    if (!user?.uid || !db) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const rows = [];
      let lastDoc = null;
      // Walk newest → oldest until every tbl_notifications row for this user is loaded.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const parts = [
          collection(db, 'tbl_notifications'),
          where('userId', '==', user.uid),
          orderBy('timestamp', 'desc'),
          limit(FETCH_BATCH),
        ];
        if (lastDoc) parts.splice(3, 0, startAfter(lastDoc));
        const snap = await getDocs(query(...parts));
        if (snap.empty) break;
        snap.docs.forEach((docSnap) => {
          const data = docSnap.data() || {};
          rows.push({
            id: docSnap.id,
            ...data,
            timestamp: toDate(data.timestamp) || new Date(0),
          });
        });
        lastDoc = snap.docs[snap.docs.length - 1];
        if (snap.size < FETCH_BATCH) break;
      }
      setNotifications(rows);
    } catch (err) {
      console.error('Notification history load failed:', err);
      setError(err);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const formatTimestamp = (timestamp) => {
    const date = toDate(timestamp);
    if (!date) return '';
    try {
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (e) {
      console.error('Error formatting timestamp:', e);
      return '';
    }
  };

  const filteredNotifications = useMemo(() => {
    let filtered = notifications;

    if (selectedDate) {
      filtered = filtered.filter((notification) => {
        const notificationDate = toDate(notification.timestamp);
        return notificationDate && isSameDay(notificationDate, selectedDate);
      });
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((notification) => {
        const message = notification.log || notification.message || '';
        return message.toLowerCase().includes(q);
      });
    }

    return filtered;
  }, [notifications, selectedDate, searchTerm]);

  const notificationDates = useMemo(() => {
    const dates = new Set();
    notifications.forEach((notification) => {
      const date = toDate(notification.timestamp);
      if (date) dates.add(format(date, 'yyyy-MM-dd'));
    });
    return Array.from(dates).sort().reverse();
  }, [notifications]);

  // Jump the calendar to a month that actually has notifications when opening it.
  useEffect(() => {
    if (!showCalendar || selectedDate || !notificationDates.length) return;
    const newest = toDate(`${notificationDates[0]}T12:00:00`);
    if (newest) setCalendarMonth(startOfMonth(newest));
  }, [showCalendar, selectedDate, notificationDates]);

  const totalPages = Math.max(1, Math.ceil(filteredNotifications.length / PAGE_SIZE));
  const paginatedNotifications = filteredNotifications.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [selectedDate, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCalendar && !event.target.closest('.calendar-container')) {
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCalendar]);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(calendarMonth);
    const monthEnd = endOfMonth(calendarMonth);
    const gridStart = new Date(monthStart);
    gridStart.setDate(gridStart.getDate() - monthStart.getDay());
    const days = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + i);
      const dateStr = format(date, 'yyyy-MM-dd');
      days.push({
        date,
        dateStr,
        hasNotifications: notificationDates.includes(dateStr),
        isSelected: selectedDate && isSameDay(date, selectedDate),
        isCurrentMonth: date >= monthStart && date <= monthEnd,
      });
    }
    return days;
  }, [calendarMonth, notificationDates, selectedDate]);

  const oldestLabel = notificationDates.length
    ? format(new Date(`${notificationDates[notificationDates.length - 1]}T12:00:00`), 'MMM yyyy')
    : null;
  const newestLabel = notificationDates.length
    ? format(new Date(`${notificationDates[0]}T12:00:00`), 'MMM yyyy')
    : null;

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Notification History</h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div className="relative calendar-container">
              <button
                onClick={() => setShowCalendar(!showCalendar)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white hover:bg-gray-600 transition-colors"
              >
                <Calendar className="w-4 h-4" />
                {selectedDate ? format(selectedDate, 'MMM dd, yyyy') : 'Select Date'}
                {selectedDate && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedDate(null);
                    }}
                    className="ml-2 text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </button>

              {showCalendar && (
                <div className="absolute top-full left-0 mt-2 bg-gray-800 border border-gray-600 rounded-lg p-4 z-10 shadow-lg min-w-[280px]">
                  <div className="flex items-center justify-between mb-3">
                    <button
                      type="button"
                      onClick={() => setCalendarMonth((m) => subMonths(m, 1))}
                      className="p-1 rounded hover:bg-gray-700 text-gray-300"
                      aria-label="Previous month"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="text-sm font-medium">{format(calendarMonth, 'MMMM yyyy')}</div>
                    <button
                      type="button"
                      onClick={() => setCalendarMonth((m) => addMonths(m, 1))}
                      className="p-1 rounded hover:bg-gray-700 text-gray-300"
                      aria-label="Next month"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-7 gap-1 text-xs mb-2">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                      <div key={day} className="text-center text-gray-400 py-1 font-medium">
                        {day}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-1 text-xs">
                    {calendarDays.map(({ date, dateStr, hasNotifications, isSelected, isCurrentMonth }) => (
                      <button
                        key={dateStr}
                        type="button"
                        onClick={() => {
                          if (!hasNotifications) return;
                          setSelectedDate(isSelected ? null : date);
                          setShowCalendar(false);
                        }}
                        disabled={!hasNotifications}
                        className={`p-2 rounded text-xs ${
                          isSelected
                            ? 'bg-purple-600 text-white'
                            : hasNotifications
                              ? 'text-white hover:bg-gray-700'
                              : 'text-gray-500 cursor-not-allowed'
                        } ${!isCurrentMonth ? 'opacity-50' : ''}`}
                      >
                        {format(date, 'd')}
                      </button>
                    ))}
                  </div>

                  <div className="mt-3 pt-2 border-t border-gray-600 text-xs text-gray-400">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-3 h-3 bg-purple-600 rounded" />
                      <span>Selected date</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-gray-600 rounded" />
                      <span>Dates with notifications</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {(selectedDate || searchTerm) && (
            <div className="flex items-center gap-2 mt-4 text-sm">
              <span className="text-gray-400">Active filters:</span>
              {selectedDate && (
                <span className="bg-purple-600 text-white px-2 py-1 rounded">
                  Date: {format(selectedDate, 'MMM dd, yyyy')}
                </span>
              )}
              {searchTerm && (
                <span className="bg-blue-600 text-white px-2 py-1 rounded">
                  Search: &quot;{searchTerm}&quot;
                </span>
              )}
              <button
                onClick={() => {
                  setSelectedDate(null);
                  setSearchTerm('');
                }}
                className="text-gray-400 hover:text-white"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        <div className="bg-gray-800 rounded-lg p-4">
          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-400">Loading notifications...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-400">Error loading notifications: {error.message}</p>
              <button
                type="button"
                onClick={loadHistory}
                className="mt-3 text-sm text-purple-300 hover:text-purple-200"
              >
                Retry
              </button>
            </div>
          ) : filteredNotifications.length > 0 ? (
            <>
              <div className="mb-4 text-sm text-gray-400">
                Showing {filteredNotifications.length} of {notifications.length} notifications
                {oldestLabel && newestLabel && (
                  <span className="ml-2">
                    ({oldestLabel === newestLabel ? oldestLabel : `${oldestLabel} – ${newestLabel}`})
                  </span>
                )}
                {(selectedDate || searchTerm) && <span className="ml-2">(filtered)</span>}
              </div>
              <div className="space-y-4">
                {paginatedNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="border-b border-gray-700 pb-4 last:border-b-0 last:pb-0"
                  >
                    <p className="text-white text-sm whitespace-pre-wrap mb-1">
                      {notification.log || notification.message}
                    </p>
                    <span className="text-xs text-gray-400">
                      {formatTimestamp(notification.timestamp)}
                      {toDate(notification.timestamp)
                        ? ` · ${format(toDate(notification.timestamp), 'MMM d, yyyy h:mm a')}`
                        : ''}
                    </span>
                  </div>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="flex justify-center flex-wrap gap-2 mt-6">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPage(i + 1)}
                      className={`px-3 py-1 rounded ${page === i + 1 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-800'}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-400">
                {notifications.length > 0 ? 'No notifications match your filters' : 'No notifications yet'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryPage;
